import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Facebook } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = React.useState('');
  const [subscribing, setSubscribing] = React.useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/email-subscribe`, { email: email.trim(), source: 'footer' });
      toast.success('Subscribed! Check your inbox for 10% off.');
      setEmail('');
    } catch { toast.error('Failed to subscribe'); }
    finally { setSubscribing(false); }
  };

  return (
    <footer className="bg-black border-t border-white/10" data-testid="footer">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_print-store-6/artifacts/i6m0cwtm_Logo%20%281%29.png" 
                alt="PosterFire" 
                className="h-12 w-auto mb-4"
              />
            </Link>
            <p className="text-sm text-white/50 leading-relaxed mb-6">
              Premium art prints for your walls. From cinema legends to modern abstracts, find the perfect poster to transform your space.
            </p>
            <div className="flex gap-4">
              <a href="#" className="p-2 bg-white/10 hover:bg-fire transition-colors text-white" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-white/10 hover:bg-fire transition-colors text-white" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-white/10 hover:bg-fire transition-colors text-white" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3 className="font-heading font-bold text-sm text-white uppercase tracking-widest mb-6">Shop</h3>
            <ul className="space-y-3">
              <li><Link to="/shop" className="text-sm text-white/50 hover:text-fire transition-colors">All Posters</Link></li>
              <li><Link to="/shop?category=movies" className="text-sm text-white/50 hover:text-fire transition-colors">Cinema Classics</Link></li>
              <li><Link to="/shop?category=music" className="text-sm text-white/50 hover:text-fire transition-colors">Music & Gigs</Link></li>
              <li><Link to="/shop?category=sports" className="text-sm text-white/50 hover:text-fire transition-colors">Sports</Link></li>
              <li><Link to="/shop?category=art" className="text-sm text-white/50 hover:text-fire transition-colors">Abstract Art</Link></li>
              <li><Link to="/shop?category=photography" className="text-sm text-white/50 hover:text-fire transition-colors">Photography</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-heading font-bold text-sm text-white uppercase tracking-widest mb-6">Support</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-sm text-white/50 hover:text-fire transition-colors">Shipping Info</a></li>
              <li><Link to="/refund-policy" className="text-sm text-white/50 hover:text-fire transition-colors">Refund Policy</Link></li>
              <li><Link to="/copyright-policy" className="text-sm text-white/50 hover:text-fire transition-colors">Copyright Policy</Link></li>
              <li><a href="#" className="text-sm text-white/50 hover:text-fire transition-colors">FAQs</a></li>
              <li><a href="mailto:support@posterfire.com" className="text-sm text-white/50 hover:text-fire transition-colors">Contact Us</a></li>
              <li><Link to="/blog" className="text-sm text-white/50 hover:text-fire transition-colors" data-testid="footer-blog-link">Blog</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-heading font-bold text-sm text-white uppercase tracking-widest mb-6">Stay Updated</h3>
            <p className="text-sm text-white/50 mb-4">Get 10% off your first order when you subscribe.</p>
            <form className="flex flex-col gap-3" onSubmit={handleSubscribe}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="bg-white/10 border-0 py-3 px-4 text-sm text-white placeholder:text-white/30 focus:ring-2 focus:ring-fire outline-none"
                data-testid="newsletter-email"
              />
              <button type="submit" disabled={subscribing} className="bg-fire text-white py-3 px-4 text-sm font-bold uppercase tracking-wider hover:bg-fire-light transition-colors disabled:opacity-50" data-testid="newsletter-submit">
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* Payment Methods + Shipping */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* USA Shipping */}
            <div className="flex items-center gap-3" data-testid="usa-shipping">
              <img src="https://static.prod-images.emergentagent.com/jobs/05b8c3c3-3029-469c-80b3-e8c21e27762e/images/2e135fccde0b7ea1a7b3c5c2772f60332fba02991e9d0c992da3cb6c458fa727.png" alt="USA Flag" className="w-8 h-6 object-contain flex-shrink-0" />
              <span className="text-sm text-white/60 font-medium tracking-wide">All Orders Ship from the USA</span>
            </div>

            {/* Payment Methods - PayPal Accepted */}
            <div className="flex items-center gap-3" data-testid="payment-methods">
              <span className="text-xs text-white/40 uppercase tracking-wider mr-1">We Accept</span>
              {/* PayPal */}
              <div className="w-12 h-8 bg-white rounded flex items-center justify-center" title="PayPal">
                <svg viewBox="0 0 48 16" className="w-9 h-3.5">
                  <text x="4" y="12" fill="#003087" fontWeight="bold" fontSize="9" fontFamily="Arial, sans-serif">Pay</text>
                  <text x="24" y="12" fill="#009CDE" fontWeight="bold" fontSize="9" fontFamily="Arial, sans-serif">Pal</text>
                </svg>
              </div>
              {/* Venmo */}
              <div className="w-12 h-8 bg-[#3D95CE] rounded flex items-center justify-center" title="Venmo">
                <span className="text-white text-[8px] font-bold tracking-wide">Venmo</span>
              </div>
              {/* Visa via PayPal */}
              <div className="w-12 h-8 bg-white rounded flex items-center justify-center" title="Visa">
                <svg viewBox="0 0 48 16" className="w-9 h-4">
                  <text x="2" y="13" fill="#1A1F71" fontWeight="bold" fontSize="14" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text>
                </svg>
              </div>
              {/* Mastercard via PayPal */}
              <div className="w-12 h-8 bg-white rounded flex items-center justify-center" title="Mastercard">
                <svg viewBox="0 0 32 20" className="w-8 h-5">
                  <circle cx="11" cy="10" r="8" fill="#EB001B" opacity="0.9"/>
                  <circle cx="21" cy="10" r="8" fill="#F79E1B" opacity="0.9"/>
                  <path d="M16 4a8 8 0 0 0 0 12 8 8 0 0 0 0-12z" fill="#FF5F00"/>
                </svg>
              </div>
              {/* Amex via PayPal */}
              <div className="w-12 h-8 bg-[#006FCF] rounded flex items-center justify-center" title="American Express">
                <span className="text-white text-[8px] font-bold leading-tight text-center">AMEX</span>
              </div>
              {/* Discover via PayPal */}
              <div className="w-12 h-8 bg-white rounded flex items-center justify-center" title="Discover">
                <svg viewBox="0 0 48 16" className="w-9 h-3">
                  <circle cx="28" cy="8" r="7" fill="#F76F20"/>
                  <text x="1" y="12" fill="#111" fontWeight="bold" fontSize="8" fontFamily="Arial, sans-serif">DISC</text>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/30">&copy; {currentYear} PosterFire. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-white/30 hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="text-sm text-white/30 hover:text-white transition-colors">Terms of Service</a>
            <Link to="/aaa" className="text-[10px] text-white/10 hover:text-white/30 transition-colors" data-testid="hidden-admin-link">AAA</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
